import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import type { CollisionSystem } from '../../systems/CollisionSystem';

export interface PlatformConfig extends BehaviorConfig {
  /** Gravity strength (pixels/sec²). REQUIRED */
  gravity: number;
  /** Maximum fall speed (pixels/sec). REQUIRED */
  maxFallSpeed: number;
}

/**
 * PlatformBehavior - Makes an Entity affected by gravity and collide with solids
 *
 * - Enables gravity on the entity
 * - Collides with solidEntities (TileSprites, Entities with SolidBehavior)
 * - Detects ground via body.blocked.down
 * - Emits 'platformCollisionSide' when hitting walls
 *
 * REQUIRED CONFIG (must come from JSON, no defaults):
 * - gravity: number (pixels/sec²)
 * - maxFallSpeed: number (pixels/sec)
 *
 * Use this for entities that should stand on the ground (cows, humans, etc.)
 */
export class PlatformBehavior extends Behavior {
  private gravity: number;
  private maxFallSpeed: number;
  private fallVelocity: number = 0;
  private isGrounded: boolean = false;
  private collider: Phaser.Physics.Arcade.Collider | null = null;
  // Punto seguro para rescatar entidades si atraviesan el suelo por frames raros
  private lastSafeX: number;
  private lastSafeY: number;
  private lastRecoveryAt: number = 0;

  constructor(entity: Entity, config: PlatformConfig) {
    super(entity, config);
    this.lastSafeX = entity.x;
    this.lastSafeY = entity.y;

    // LOUD FAIL: Validate required config
    const entityName = entity.texture?.key || 'unknown';
    let hasError = false;

    if (config.gravity === undefined) {
      console.error('[PlatformBehavior] Missing required config: gravity', {
        entity: entityName,
        fix: 'Add behaviors.platform.gravity to entity JSON',
      });
      hasError = true;
    }

    if (config.maxFallSpeed === undefined) {
      console.error('[PlatformBehavior] Missing required config: maxFallSpeed', {
        entity: entityName,
        fix: 'Add behaviors.platform.maxFallSpeed to entity JSON',
      });
      hasError = true;
    }

    if (hasError) {
      // Disable behavior - entity won't have platform physics
      // This makes the error VERY visible during development
      this.enabled = false;
      this.gravity = 0;
      this.maxFallSpeed = 0;
      return;
    }

    this.gravity = config.gravity;
    this.maxFallSpeed = config.maxFallSpeed;

    // Register state in entity.state.behaviors (namespaced pattern)
    this.entity.state.behaviors.platform = { isGrounded: false };

    this.setupPhysics();
    this.setupCollision();

    // Ajuste inicial: al spawnear, intentar alinear al suelo para evitar caídas aleatorias.
    this.scene.time.delayedCall(0, () => {
      if (!this.entity.active) return;
      const body = this.entity.body as Phaser.Physics.Arcade.Body | null;
      if (!body) return;
      this.trySnapToGround(body, undefined, true);
    });
  }

  private setupPhysics(): void {
    const body = this.entity.body as Phaser.Physics.Arcade.Body;
    if (!body) {
      console.error('[PlatformBehavior] Entity has no physics body', {
        entity: this.entity.texture?.key,
      });
      return;
    }

    // Enable gravity - we handle it manually in update for precise control
    body.setAllowGravity(false);
    body.setImmovable(false);
  }

  private setupCollision(): void {
    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) {
      console.error('[PlatformBehavior] CollisionSystem not found in registry');
      return;
    }

    // Get solidEntities group for collision
    const solidEntities = collisionSystem.getSolidEntities();
    if (!solidEntities) {
      console.error('[PlatformBehavior] solidEntities group not available');
      return;
    }

    // Setup collider with solidEntities
    // The collider causes Phaser to set body.blocked.* which we read in update()
    this.collider = this.scene.physics.add.collider(
      this.entity,
      solidEntities
    );
  }

  /**
   * Update - apply gravity and check for ground using body.blocked
   *
   * Uses Phaser's native body.blocked.* for directional collision detection:
   * - blocked.down → entity is grounded
   * - blocked.left/right → side collision, emits event for WalkerBehavior
   *
   * State-based communication with AbductableBehavior:
   * - abductionState === 'lifting' → being abducted, don't apply gravity
   * - abductionState === 'released' → falling after abduction, reset to 'idle' when landing
   */
  override update(time: number, delta: number): void {
    if (!this.enabled) return;

    const body = this.entity.body as Phaser.Physics.Arcade.Body;
    if (!body) {
      console.error('[PlatformBehavior] Entity has no physics body');
      return;
    }

    // Check abduction state via entity.state.behaviors (namespaced communication)
    const abductionState = this.entity.state.behaviors.abductable?.state;

    // Being abducted - don't apply gravity, AbductableBehavior handles lift
    if (abductionState === 'lifting') {
      this.fallVelocity = 0;
      return;
    }

    // Si está muerto, no aplicar gravedad (evita que caiga fuera del mundo)
    const entityAny = this.entity as unknown as { states?: { special?: string }; isDead?: () => boolean };
    const isDead = entityAny.isDead?.() ?? (entityAny.states?.special === 'death');
    if (isDead) {
      this.fallVelocity = 0;
      body.velocity.y = 0;
      return;
    }

    const wasGrounded = this.isGrounded;
    const deltaSeconds = delta / 1000;

    // Detect DOWN collision = on the ground
    // body.blocked is set by Phaser's collider after collision resolution
    this.isGrounded = body.blocked.down || body.touching.down;
    this.entity.state.behaviors.platform!.isGrounded = this.isGrounded;

    // Mientras esté en suelo, guardar posición segura continuamente (sirve para cualquier entidad).
    if (this.isGrounded) {
      this.lastSafeX = this.entity.x;
      this.lastSafeY = this.entity.y;
    }

    // Just landed
    if (this.isGrounded && !wasGrounded) {
      this.fallVelocity = 0;
      body.velocity.y = 0;
      this.lastSafeX = this.entity.x;
      this.lastSafeY = this.entity.y;

      // Reset abduction state when landing after release
      if (abductionState === 'released' && this.entity.state.behaviors.abductable) {
        this.entity.state.behaviors.abductable.state = 'idle';
      }

      // Emit event for other behaviors (WalkerBehavior listens to resume walking)
      this.scene.events.emit('entityLanded', {
        entity: this.entity,
      });
    }

    // Detect SIDE collision → notify WalkerBehavior to turn around
    if (body.blocked.left || body.blocked.right) {
      this.scene.events.emit('platformCollisionSide', {
        entity: this.entity,
        direction: body.blocked.left ? 'left' : 'right',
      });
    }

    // Apply gravity if not grounded
    if (!this.isGrounded) {
      this.fallVelocity += this.gravity * deltaSeconds;
      this.fallVelocity = Math.min(this.fallVelocity, this.maxFallSpeed);
      body.velocity.y = this.fallVelocity;

      // Anti-tunel: si ya cruzó el suelo en este frame, pegarla al piso.
      this.trySnapToGround(body, abductionState);

      // Failsafe: si una entidad se va por debajo de cámara/mundo, reubicarla al suelo más cercano.
      this.tryRecoverFromVoid(body, abductionState);
    }
  }

  /**
   * Evita que una entidad atraviese el suelo cuando cae rápido o en frames inestables.
   */
  private trySnapToGround(
    body: Phaser.Physics.Arcade.Body,
    abductionState: string | undefined,
    force: boolean = false
  ): void {
    if (abductionState === 'lifting' || abductionState === 'released') return;
    if (!force && body.velocity.y <= 0) return;

    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    if (!collisionSystem) return;

    // Buscar suelo en la columna actual (consultando toda la columna).
    const groundY = collisionSystem.getGroundYBelow(this.entity.x, -10000);
    if (groundY === null) return;

    const groundOffset = this.entity.displayHeight * (1 - this.entity.originY);
    const bottomY = this.entity.y + groundOffset;

    // Si el pie ya pasó (o casi toca) el suelo, snap directo
    if (force || bottomY >= groundY - 2) {
      this.entity.y = groundY - groundOffset;
      body.velocity.y = 0;
      this.fallVelocity = 0;
      this.isGrounded = true;
      this.entity.state.behaviors.platform!.isGrounded = true;
      this.lastSafeX = this.entity.x;
      this.lastSafeY = this.entity.y;

      this.scene.events.emit('entityLanded', { entity: this.entity });
    }
  }

  /**
   * Evita que algunas entidades atraviesen el suelo y se pierdan fuera de pantalla.
   * Solo aplica cuando NO están siendo abducidas.
   */
  private tryRecoverFromVoid(
    body: Phaser.Physics.Arcade.Body,
    abductionState: string | undefined
  ): void {
    if (abductionState === 'lifting' || abductionState === 'released') return;

    const now = this.scene.time.now;
    if (now - this.lastRecoveryAt < 250) return;

    const camera = this.scene.cameras.main;
    const worldBottom = this.scene.physics.world.bounds.bottom;
    const belowCamera = this.entity.y > camera.scrollY + camera.height + 220;
    const belowWorld = this.entity.y > worldBottom + 120;
    if (!belowCamera && !belowWorld) return;

    const collisionSystem = this.scene.registry.get('collisionSystem') as CollisionSystem | undefined;
    const groundYAtCurrentX = collisionSystem?.getGroundYBelow(this.entity.x, -10000) ?? null;
    const groundYAtSafeX = collisionSystem?.getGroundYBelow(this.lastSafeX, -10000) ?? null;
    const targetGroundY = groundYAtCurrentX ?? groundYAtSafeX;

    if (targetGroundY !== null) {
      const groundOffset = this.entity.displayHeight * (1 - this.entity.originY);
      this.entity.y = targetGroundY - groundOffset;
      if (groundYAtCurrentX === null) {
        this.entity.x = this.lastSafeX;
      }
    } else {
      // Fallback si no encontramos suelo en esa columna
      this.entity.x = this.lastSafeX;
      this.entity.y = this.lastSafeY;
    }

    body.velocity.y = 0;
    this.fallVelocity = 0;
    this.isGrounded = true;
    this.entity.state.behaviors.platform!.isGrounded = true;
    this.lastSafeX = this.entity.x;
    this.lastSafeY = this.entity.y;
    this.lastRecoveryAt = now;

    this.scene.events.emit('entityLanded', { entity: this.entity });
  }

  /**
   * Check if entity is currently grounded
   */
  getIsGrounded(): boolean {
    return this.isGrounded;
  }

  override destroy(): void {
    // Clean up state by removing namespace
    delete this.entity.state.behaviors.platform;

    // Remove collider
    if (this.collider) {
      this.collider.destroy();
      this.collider = null;
    }

    super.destroy();
  }
}
